<script setup lang="ts">
import { ref, nextTick } from "vue";
import { onClickOutside, useEventListener } from "@vueuse/core";
import { EllipsisVertical } from "@lucide/vue";

const props = withDefaults(
  defineProps<{
    align?: "left" | "right";
  }>(),
  { align: "left" }
);

const buttonRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const open = ref(false);
const positioned = ref(false);
const menuStyle = ref<{ top: string; left: string }>({ top: "0px", left: "0px" });
const effectiveAlign = ref<"left" | "right">(props.align);

const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 4;

async function toggle() {
  open.value = !open.value;
  if (!open.value || !buttonRef.value) {
    positioned.value = false;
    return;
  }

  await nextTick();
  const triggerRect = buttonRef.value.getBoundingClientRect();
  const menuRect = menuRef.value?.getBoundingClientRect();
  const menuWidth = menuRect?.width ?? 160;
  const menuHeight = menuRect?.height ?? 0;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Decide horizontal alignment, flipping if the requested side would overflow.
  let alignRight = props.align === "right";
  if (!alignRight && triggerRect.left + menuWidth > vw - VIEWPORT_MARGIN) alignRight = true;
  if (alignRight && triggerRect.right - menuWidth < VIEWPORT_MARGIN) alignRight = false;

  // Decide vertical placement: open downward by default, flip up if it would overflow and there's room above.
  let top = triggerRect.bottom + TRIGGER_GAP;
  const overflowsBottom = top + menuHeight > vh - VIEWPORT_MARGIN;
  const fitsAbove = triggerRect.top - menuHeight - TRIGGER_GAP > VIEWPORT_MARGIN;
  if (overflowsBottom && fitsAbove) {
    top = triggerRect.top - menuHeight - TRIGGER_GAP;
  }

  effectiveAlign.value = alignRight ? "right" : "left";
  menuStyle.value = {
    top: `${top}px`,
    left: alignRight ? `${triggerRect.right}px` : `${triggerRect.left}px`,
  };
  positioned.value = true;
}

function close() {
  open.value = false;
  positioned.value = false;
}

onClickOutside(buttonRef, close, { ignore: [menuRef] });

useEventListener("scroll", close, { capture: true, passive: true });
useEventListener("resize", close);
</script>

<template>
  <button
    ref="buttonRef"
    type="button"
    aria-label="Open menu"
    class="relative flex"
    @click.stop="toggle">
    <div
      class="rounded-8 flex h-full w-full items-center justify-center bg-transparent text-slate-500 transition hover:text-slate-700">
      <!-- stroke-width 2: the ellipsis dots are tiny circles that read as faint rings at 1.5 -->
      <EllipsisVertical
        class="icon-md"
        :stroke-width="2" />
    </div>
    <Teleport to="body">
      <!-- style kept: top/left are dynamic pixel values computed from getBoundingClientRect() -->
      <div
        v-if="open"
        ref="menuRef"
        :style="{
          position: 'fixed',
          top: menuStyle.top,
          left: menuStyle.left,
          transform: effectiveAlign === 'right' ? 'translateX(-100%)' : undefined,
          visibility: positioned ? 'visible' : 'hidden',
        }"
        class="rounded-8 z-dropdown bg-slate-0 mt-1 min-w-40 overflow-hidden border border-slate-200 py-1"
        @click="close">
        <slot />
      </div>
    </Teleport>
  </button>
</template>
